import { SupernovaError, } from "../errors/SDKSupernovaError";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Bridge implementation
export class DataBridge {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(conf) {
        var _a, _b, _c, _d;
        this.isPreview = false;
        this.jobId = null;
        this.authToken = conf.accessToken;
        this.apiUrl = conf.apiUrl;
        this.aiApiUrl = conf.aiApiUrl;
        this.apiVersion = conf.apiVersion;
        this.target = conf.target;
        this.customFetchLib = (_a = conf.customFetchLib) !== null && _a !== void 0 ? _a : null;
        this.customProxyLib = (_b = conf.customProxyLib) !== null && _b !== void 0 ? _b : null;
        if (conf.jobId) {
            this.jobId = conf.jobId;
        }
        if (conf.isPreview) {
            this.isPreview = conf.isPreview;
        }
        this.requestHook = conf.requestHook;
        this.debugRequestObserver = (_c = conf.debugRequestObserver) !== null && _c !== void 0 ? _c : null;
        this.debugResponseObserver = (_d = conf.debugResponseObserver) !== null && _d !== void 0 ? _d : null;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Requests
    async delete(endpoint, payload) {
        return this.request(endpoint, payload, "DELETE");
    }
    async get(endpoint) {
        return this.request(endpoint, undefined, "GET");
    }
    async getStreamed(endpoint) {
        return this.streamedRequest(endpoint, undefined, "GET");
    }
    async getAsData(endpoint) {
        return this.request(endpoint, undefined, "GET", true);
    }
    async put(endpoint, payload) {
        return this.request(endpoint, payload, "PUT");
    }
    async post(endpoint, payload) {
        return this.request(endpoint, payload, "POST");
    }
    async postStreamed(endpoint, payload) {
        return this.streamedRequest(endpoint, payload, "POST");
    }
    async upload(endpoint, formData, method = "PUT") {
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        const config = await this.buildRequestConfig(method, undefined);
        const proxyAgent = await this.getProxyAgent();
        const invoker = await this.getFetchInvoker();
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                const headers = Object.assign({}, config.headers);
                // @ts-expect-error TS(2790): The operand of a 'delete' operator must be optiona... Remove this comment to see the full error message
                delete headers["Content-Type"];
                invoker(endpoint, {
                    method: config.method,
                    headers,
                    body: formData,
                    agent: proxyAgent,
                })
                    .then(async (response) => {
                    return this.handleResponse(response, endpoint);
                })
                    .then((jsonResponse) => {
                    resolve(jsonResponse);
                })
                    .catch((error) => {
                    reject(error);
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    async request(endpoint, payload, method, asData = false) {
        const config = await this.buildRequestConfig(method, "application/json");
        const proxyAgent = await this.getProxyAgent();
        const invoker = await this.getFetchInvoker();
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                invoker(endpoint, {
                    method: config.method,
                    headers: config.headers,
                    body: payload ? JSON.stringify(payload) : null,
                    agent: proxyAgent,
                })
                    .then(async (response) => {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    return this.handleResponse(response, endpoint, asData);
                })
                    .then((response) => {
                    resolve(response);
                })
                    .catch((error) => {
                    reject(error);
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    async streamedRequest(endpoint, payload, method, asData = false) {
        const config = await this.buildRequestConfig(method, "text/plain");
        const proxyAgent = await this.getProxyAgent();
        const invoker = await this.getFetchInvoker();
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                invoker(endpoint, {
                    method: config.method,
                    headers: config.headers,
                    body: payload ? JSON.stringify(payload) : null,
                    agent: proxyAgent,
                })
                    .then(async (response) => {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    return this.handleStreamedResponse(response, endpoint);
                })
                    .then((response) => {
                    resolve(response);
                })
                    .catch((error) => {
                    reject(error);
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    async handleResponse(response, endpoint, asData = false) {
        if (!response.ok) {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const errorResponse = await response.json();
            if (errorResponse) {
                // Server responded with proper error in JSON format
                const error = SupernovaError.fromNetworkResponse(errorResponse, response.status, endpoint);
                throw error;
            }
            else {
                // Server didn't respond or responded with invalid JSON
                const error = SupernovaError.fromNoNetworkResponse(endpoint);
                throw error;
            }
        }
        if (asData) {
            return response.arrayBuffer();
        }
        return response.json();
    }
    async handleStreamedResponse(response, endpoint) {
        var _a;
        if (!response.ok) {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const errorResponse = await response.json();
            if (errorResponse) {
                // Server responded with proper error in JSON format
                const error = SupernovaError.fromNetworkResponse(errorResponse, response.status, endpoint);
                throw error;
            }
            else {
                // Server didn't respond or responded with invalid JSON
                const error = SupernovaError.fromNoNetworkResponse(endpoint);
                throw error;
            }
        }
        const reader = (_a = response.body) === null || _a === void 0 ? void 0 : _a.getReader();
        if (!reader) {
            throw new Error("Failed to get reader from response, likely using a non-streaming endpoint with streaming handler");
        }
        return reader;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Request configuration
    async buildRequestConfig(method, contentType) {
        var _a;
        const config = {
            method,
            headers: Object.assign(Object.assign(Object.assign({}, (contentType && { "Content-Type": contentType })), { Authorization: undefined, "x-sn-exporter-preview": this.isPreview ? "true" : undefined, "x-sn-export-job": this.jobId ? this.jobId : undefined }), (((_a = process === null || process === void 0 ? void 0 : process.env) === null || _a === void 0 ? void 0 : _a.SUPERNOVA_API_SECRET) && {
                "X-SN-API-SECRET": process.env.SUPERNOVA_API_SECRET,
            })),
        };
        let skipAuth = false;
        if (this.requestHook) {
            const hookResult = await this.requestHook(config);
            if (hookResult && hookResult.skipDefaultAuth) {
                skipAuth = true;
            }
        }
        if (!skipAuth) {
            const token = this.authToken;
            // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'undefined... Remove this comment to see the full error message
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    }
    async getProxyAgent() {
        return this.customProxyLib ? this.customProxyLib : undefined;
    }
    getFetchInvoker() {
        return this.customFetchLib ? this.customFetchLib : fetch;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Endpoints
    getAIEndpoint(fragment) {
        return `${this.aiApiUrl}/${fragment}`;
    }
    getApiEndpoint(fragment) {
        return `${this.apiUrl}/${fragment}`;
    }
    getBaseEndpoint(fragment) {
        if (fragment) {
            return `${this.apiUrl}/${fragment}`;
        }
        return `${this.apiUrl}`;
    }
    getPublicEndpoint(fragment) {
        return `${this.apiUrl}/public/${fragment}`;
    }
    getAclEndpoint(resource) {
        if (!resource) {
            return `${this.getPublicEndpoint("acl")}`;
        }
        return `${this.getPublicEndpoint("acl")}/${resource}`;
    }
    getMCPStreamEndpoint(fragment) {
        if (fragment) {
            return this.getApiEndpoint(`mcp/streams/${fragment}`);
        }
        return this.getApiEndpoint("mcp/streams");
    }
    getUserEndpoint(userId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/users/${userId}/${fragment}`;
        }
        return `${this.apiUrl}/users/${userId}`;
    }
    getUserWorkspaceEndpoint(userId, wsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/users/${userId}/workspaces/${wsId}/${fragment}`;
        }
        return `${this.apiUrl}/users/${userId}/workspaces/${wsId}`;
    }
    getWorkspaceEndpoint(wsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/workspaces/${wsId}/${fragment}`;
        }
        return `${this.apiUrl}/workspaces/${wsId}`;
    }
    getWorkspaceIntegrationCredentialEndpoint(wsId, integrationId, credentialId) {
        return `${this.getWorkspaceEndpoint(wsId, null)}/integrations/${integrationId}/credentials/${credentialId}`;
    }
    getWorkspaceIntegrationEndpoint(wsId, integrationId) {
        return `${this.getWorkspaceEndpoint(wsId, null)}/integrations/${integrationId}`;
    }
    getWorkspaceIntegrationGitProvidersEndpoint(wsId, integrationId, operation) {
        return `${this.getWorkspaceIntegrationEndpoint(wsId, integrationId)}/git/${operation}`;
    }
    getDesignSystemEndpoint(dsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/design-systems/${dsId}/${fragment}`;
        }
        return `${this.apiUrl}/design-systems/${dsId}`;
    }
    getVersionEndpoint(dsId, dsVersionId, fragment) {
        if (fragment) {
            return `${this.getDesignSystemEndpoint(dsId, `versions/${dsVersionId}/${fragment}`)}`;
        }
        return `${this.getDesignSystemEndpoint(dsId, `versions/${dsVersionId}`)}`;
    }
    getVersionCreationJobEndpoint(dsId, jobId) {
        return `${this.getDesignSystemEndpoint(dsId, `versions/job/${jobId}`)}`;
    }
    getVersionCreationJobsEndpoint(dsId) {
        return `${this.getDesignSystemEndpoint(dsId, `versions/jobs`)}`;
    }
}
